/**
 * Design System - Badge Component
 *
 * Small status indicators and labels
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-sm font-medium transition-all',
  {
    variants: {
      variant: {
        default: 'bg-[#F5F5F4] text-[#1C1917] border border-[#E7E5E4]',
        primary: 'bg-[#FDE047] text-[#1C1917] border border-[#F6EBB5]',
        success: 'bg-[#DCF5E6] text-[#16A34A] border border-[#BFE5CD]',
        error: 'bg-[#FEE2E2] text-[#DC2626] border border-[#FECACA]',
        warning: 'bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A]',
        info: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE]',
        outlined: 'bg-transparent text-[#1C1917] border border-[#D6D3D1]',
      },
      size: {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-1 text-sm',
        lg: 'px-3 py-1.5 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * Icon to be rendered before the badge text
   */
  icon?: React.ReactNode;
  /**
   * Badge content
   */
  children?: React.ReactNode;
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, icon, children, ...props }, ref) => {
    return (
      <span
        className={cn(badgeVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {icon && <span className='inline-flex shrink-0'>{icon}</span>}
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';
